import { useParams } from '@tanstack/react-router';
import DestinationPushesTable from '@zeus/core/drilldownTables/DestinationPushesTable';
import { BreadcrumbItem, ActionButton } from '@zeus/core/layout/MainLayout';
import PageContent from '@zeus/core/layout/PageContent';
import AsyncPageContainer from '_/common/components/layout/AsyncPageContainer';
import { useDeliveryContext } from '_/contexts/DeliveryContext';
import { useFlowContext } from '_/contexts/FlowContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { DestinationPushStatus } from '_/features/destinationPushes/common/constants';
import { useDelivery, useDeliverySources, useDeliveryDestinationPushes } from '_/hooks/useDeliveries';
import { useRetryDestinationPush, useRevertDestinationPush } from '_/hooks/useDestinationPushes';
import { formatDateTime } from '_/utils/dateUtils';
import { Radio, Space, Modal } from 'antd';
import { keyBy } from 'lodash';
import React, { ReactElement, useState, useMemo, useEffect, useCallback } from 'react';

interface DestinationPushesSpotlightProps {
  selectedFilter?: string;
}

function DestinationPushesSpotlight({ selectedFilter }: DestinationPushesSpotlightProps): ReactElement {
  const params = useParams({ strict: false }) as Partial<{ deliveryId: string }>;
  const deliveryId = params.deliveryId || '';
  const { setActionButtons, setOnRefresh, setRefreshLoading, setBreadcrumbs } = useTopHeader();
  const { selectedOrganization, selectedOrgSlug } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();
  const { selectedFlow, flowSlug } = useFlowContext();
  const { selectedDelivery: delivery } = useDeliveryContext();

  const allStatuses = 'status||in||' + Object.values(DestinationPushStatus).join(',');
  const [pushId, setPushId] = useState('');
  const [filter, setFilter] = useState(selectedFilter || allStatuses);
  const [isRetryDestinationModalVisible, setIsRetryDestinationModalVisible] = useState(false);
  const [isRevertDestinationModalVisible, setIsRevertDestinationModalVisible] = useState(false);

  // Fetch data using React Query hooks
  const { data: selectedDelivery, isLoading: isFetchingDelivery } = useDelivery(deliveryId);
  const { data: deliverySources = [], isLoading: isFetchingDeliverySources } = useDeliverySources(deliveryId);
  const { data: destinationPushesData, isFetching, refetch } = useDeliveryDestinationPushes(deliveryId, filter);

  // React Query mutations for retry and revert
  const retryMutation = useRetryDestinationPush();
  const revertMutation = useRevertDestinationPush();

  // Transform destination pushes (replicate getDestinationPushesTable selector)
  const destinationPushes = useMemo(() => {
    const rawPushes = destinationPushesData?.items || [];
    if (!rawPushes.length || !deliverySources.length) return [];

    const sources = keyBy(deliverySources, 'sourceId');
    return rawPushes.map((destinationPush: any) => {
      const deliverySource = sources[destinationPush.snapshot?.sourceId];
      return {
        ...destinationPush,
        selectedSourceSlug: deliverySource?.source?.slug,
        selectedSourceName: deliverySource?.source?.name,
        selectedCollectionSlug: deliverySource?.source?.collection?.slug,
        snapshotId: destinationPush.snapshot?.id,
        destinationName: destinationPush.destination?.name,
        startedAt: formatDateTime(destinationPush.snapshot?.startedAt),
        completedAt: formatDateTime(destinationPush?.completedAt),
      };
    });
  }, [destinationPushesData, deliverySources]);

  const cursor = destinationPushesData?.cursor;

  const fetchDestinationPushes = async (destinationPushFilter?: string) => {
    if (destinationPushFilter) {
      setFilter(destinationPushFilter);
    }
    // React Query will refetch automatically when filter changes
  };

  const handleRefreshClick = useCallback(() => {
    refetch();
  }, [refetch]);

  const setDestinationPushId = (pushId: string) => {
    setPushId(pushId);
  };

  const toggleDestinationPushModal = (type: string, pushId?: string) => {
    if (type === 'revert') {
      if (pushId) setDestinationPushId(pushId);
      setIsRevertDestinationModalVisible((prev) => !prev);
    } else if (type === 'retry') {
      if (pushId) setDestinationPushId(pushId);
      setIsRetryDestinationModalVisible((prev) => !prev);
    }
  };

  const revertDestinationPushConfirm = async () => {
    await revertMutation.mutateAsync(pushId);
    setIsRevertDestinationModalVisible(false);
    handleRefreshClick();
  };

  const retryDestinationPushConfirm = async () => {
    await retryMutation.mutateAsync(pushId);
    setIsRetryDestinationModalVisible(false);
    handleRefreshClick();
  };

  const handleDestinationPushFilter = (e: any) => {
    const filter = e.target.value;
    if (!filter) {
      fetchDestinationPushes(allStatuses);
      return;
    }

    fetchDestinationPushes(filter);
  };

  const actionButtons: ActionButton[] = useMemo(() => [], []);

  const fetchNextSetOfDestinationPushes = () => {
    // Pagination can be implemented with infinite query if needed
    // For now, this is a placeholder
  };

  // Set up breadcrumbs
  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
    ];

    if (flowSlug && selectedProjectSlug) {
      breadcrumbs.push(
        { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
        {
          title: selectedProject?.name || '',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
        },
        { title: 'Flows', href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows` },
        {
          title: selectedFlow?.name || 'Flow',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${flowSlug}`,
        },
        {
          title: 'Deliveries',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${flowSlug}/deliveries`,
        },
        {
          title: delivery?.name || delivery?.id || 'Delivery',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${flowSlug}/deliveries/${deliveryId}`,
        },
      );
    } else {
      breadcrumbs.push(
        { title: 'Deliveries', href: `/orgs/${selectedOrgSlug}/deliveries` },
        {
          title: delivery?.name || delivery?.id || 'Delivery',
          href: `/orgs/${selectedOrgSlug}/deliveries/${deliveryId}`,
        },
      );
    }

    breadcrumbs.push({ title: 'Destination Pushes' });

    setBreadcrumbs(breadcrumbs);
    setActionButtons(actionButtons);
    setOnRefresh(handleRefreshClick);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
      setOnRefresh(undefined);
    };
  }, [
    setBreadcrumbs,
    setActionButtons,
    setOnRefresh,
    actionButtons,
    handleRefreshClick,
    selectedOrganization,
    selectedOrgSlug,
    selectedProject,
    selectedProjectSlug,
    selectedFlow,
    flowSlug,
    delivery,
    deliveryId,
  ]);

  // Update refresh loading state
  useEffect(() => {
    setRefreshLoading(isFetching);
  }, [isFetching, setRefreshLoading]);

  return (
    <AsyncPageContainer isLoading={isFetchingDelivery || isFetchingDeliverySources || !selectedDelivery}>
      <Space className="wb-fullWidth" direction="vertical">
        <PageContent>
          <Radio.Group value={filter} style={{ marginBottom: 20 }}>
            <Radio.Button value={allStatuses} onClick={handleDestinationPushFilter}>
              All
            </Radio.Button>
            <Radio.Button value="status||eq||PENDING" onClick={handleDestinationPushFilter}>
              Pending
            </Radio.Button>
            <Radio.Button value="status||eq||IN_PROGRESS" onClick={handleDestinationPushFilter}>
              In Progress
            </Radio.Button>
            <Radio.Button value="status||eq||FAILED" onClick={handleDestinationPushFilter}>
              Failed
            </Radio.Button>
            <Radio.Button value="status||eq||REVERTED" onClick={handleDestinationPushFilter}>
              Reverted
            </Radio.Button>
            <Radio.Button value="status||eq||REVERTING" onClick={handleDestinationPushFilter}>
              Reverting
            </Radio.Button>
            <Radio.Button value="status||eq||INTERNAL_FAILURE" onClick={handleDestinationPushFilter}>
              Internal Failure
            </Radio.Button>
            <Radio.Button value="status||eq||REVERT_FAILED" onClick={handleDestinationPushFilter}>
              Revert Failed
            </Radio.Button>
            <Radio.Button value="status||eq||COMPLETE" onClick={handleDestinationPushFilter}>
              Complete
            </Radio.Button>
          </Radio.Group>

          <DestinationPushesTable
            toggleDestinationPushModal={(type, pushId) => toggleDestinationPushModal(type, pushId)}
            destinationPushes={destinationPushes}
            cursor={cursor}
            fetchNextSetOfDestinationPushes={fetchNextSetOfDestinationPushes}
            isLoadingDestinations={false}
          />

          <Modal
            open={isRevertDestinationModalVisible}
            title="Revert Destination Push"
            onCancel={() => toggleDestinationPushModal('revert')}
            onOk={revertDestinationPushConfirm}
          >
            <p>Are you sure that you want to revert this push?</p>
          </Modal>

          <Modal
            open={isRetryDestinationModalVisible}
            title="Re-push to Destination"
            onCancel={() => toggleDestinationPushModal('retry')}
            onOk={retryDestinationPushConfirm}
          >
            <p>Are you sure that you want to re-push?</p>
          </Modal>
        </PageContent>
      </Space>
    </AsyncPageContainer>
  );
}

export default DestinationPushesSpotlight;
